import type { ReviewDisplayItem, ReviewsSummaryData } from "./types"

interface ReviewsSummaryProps {
  reviews?: ReviewDisplayItem[]
  summary?: ReviewsSummaryData
  emptyMessage?: string
  averageLabel?: string
  totalLabel?: (total: number) => string
  starsLabel?: (star: number) => string
}

export function calculateReviewsSummary(
  reviews: ReviewDisplayItem[]
): ReviewsSummaryData {
  const totalReviews = reviews.length
  const hasReviews = totalReviews > 0

  const average = hasReviews
    ? reviews.reduce(
        (averageAccumulator, review) => averageAccumulator + review.rating,
        0
      ) / totalReviews
    : 0

  const distribution = [5, 4, 3, 2, 1].map((star) => {
    const count = reviews.filter((review) => review.rating === star).length

    return {
      star,
      count,
      percent: totalReviews ? Math.round((count / totalReviews) * 100) : 0,
    }
  })

  return { total: totalReviews, average, distribution }
}

export function ReviewsSummary({
  reviews,
  summary,
  emptyMessage = "Este item ainda nao possui avaliacoes.",
  averageLabel = "Avaliacao media",
  totalLabel = (total) => `${total} avaliacoes`,
  starsLabel = (star) => `${star} estrelas`,
}: ReviewsSummaryProps): React.ReactNode {
  const resolvedSummary = summary ?? calculateReviewsSummary(reviews ?? [])

  if (resolvedSummary.total === 0) {
    return <p className='text-muted-foreground text-sm'>{emptyMessage}</p>
  }

  return (
    <div className='grid grid-cols-1 gap-8 md:grid-cols-2'>
      <div>
        <p className='text-muted-foreground text-sm'>{averageLabel}</p>
        <p className='font-bold text-5xl text-primary'>
          {resolvedSummary.average.toFixed(2)}
        </p>
        <p className='mt-2 text-sm'>{totalLabel(resolvedSummary.total)}</p>
      </div>

      <div className='space-y-2'>
        {resolvedSummary.distribution.map((starDistribution) => (
          <div className='flex items-center gap-3' key={starDistribution.star}>
            <span className='w-16 text-sm'>
              {starsLabel(starDistribution.star)}
            </span>
            <div className='h-2 flex-1 rounded bg-muted'>
              <div
                className='h-2 rounded bg-primary'
                style={{ width: `${starDistribution.percent}%` }}
              />
            </div>
            <span className='w-10 text-sm'>{starDistribution.percent}%</span>
          </div>
        ))}
      </div>
    </div>
  )
}
